/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import isUndefined from 'lodash.isundefined';
import {setRel} from '@ion/core/src/services/ConnectUtils';
import {Device, Devices} from '@ion/core/src/types/entities';
import {convertStatus} from '../utils/ComponentUtils';
import {BulkEditorType, getEditedItems} from './BulkEditorUtils';
import {isSCIGPSDevice} from './DeviceEditorUtils';

// enum for bulk edit device form fields ...
export enum DeviceFormFields {
  Groups = 'groups',
  Status = 'status',
  DeviceType = 'type',
  DeviceConfiguration = 'deviceConfiguration',
  ShipDate = 'shipDate'
}

// defines the prop definitions for form state event target ...
export interface TargetType {
  name: DeviceFormFields,
  value: unknown;
  required?: boolean
}

const required = {required: true};
const notRequired = {required: false};

/**
 * Provides the initial state of the user editor form, this state includes all named
 * editor fields and their requirements, as well as any initial values required.
 */
export const getInitialState = (device: Device, devices: Devices) => {
  const deviceList = devices.map(({expanded, ...rest}) => rest);
  return {
    [DeviceFormFields.Groups]              : notRequired,
    [DeviceFormFields.Status]              : notRequired,
    [DeviceFormFields.DeviceType]          : notRequired,
    [DeviceFormFields.DeviceConfiguration] : notRequired,
    [DeviceFormFields.ShipDate]            : notRequired,
    ...device,
    items                                  : deviceList
  };
};

/**
 *
 * This function takes the form data from the user form,
 * serializes it and gets passed to the service call.
 *
 */
export const serialize = (state: BulkEditorType): Devices => {
  const {items} = state;
  const serialized: BulkEditorType = {items};
  for (const [key, {value}] of Object.entries(state)) {
    if (!isUndefined(value)) {
      if (DeviceFormFields.Groups === key) {
        serialized.account = setRel(value, 'account');
      } else if (DeviceFormFields.Status === key) {
        serialized.status = convertStatus(value);
      } else if (DeviceFormFields.DeviceType === key) {
        serialized.type = value?.id ? setRel(value, 'devicetype') : (value?.href ? value : null);
      } else if (DeviceFormFields.DeviceConfiguration === key) {
        serialized.deviceConfiguration = value?.id ? setRel(value, 'deviceConfiguration') : (value?.href ? value : null);
      } else {
        serialized[key] = value;
      }
    }
  }
  return getEditedItems(serialized);
};

/**
 * The function takes an array of devices and checks all devices
 * are SCI GPS devices or not. This function will return true when all devices
 * are SCI GPS devices else false.
 *
 * ```typescript
 *
 *  import {isSCITemperaturOrMovingDevice} from '@ion/dls/src/editors/BulkDeviceEditorUtils';
 *
 *  const isSCIDevice = isSCITemperaturOrMovingDevice(devices);
 *
 * ```
 */
export const isSCITemperaturOrMovingDevice = (devices: Devices) => (
  devices?.every(device => isSCIGPSDevice(device))
);
